import { Button, Flex, Stack, Text } from '@mantine/core'
import AppModal from '../AppModal/AppModal.component'
import { notifications } from '@mantine/notifications'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'

import { Icons } from 'src/common/components/ui'

import { SESSION_CONFIG } from '../../config/session.config'
import { useTabSync } from '../../hooks/useTabSync'

import { isTokenAuthResponse } from '@v2/domains/auth/api/auth.api'
import { isCookieAuthenticationEnabled } from '@v2/domains/auth/utils/isCookieAuthenticationEnabled'
import useLogout from '@v2/domains/auth/hooks/useLogout.hook'
import { useTokenRefresh } from '@v2/domains/auth/hooks/useTokenRefresh.hook'
import { useAppSelector } from '@v2/store/hooks'

const TAB_ID = `tab-${Math.random().toString(36).substr(2, 9)}`

interface SessionTimeoutManagerProps {
  children: React.ReactNode
}

export const SessionTimeoutManager = ({
  children,
}: SessionTimeoutManagerProps) => {
  const [showModal, setShowModal] = useState(false)
  const [countdown, setCountdown] = useState(60)
  const [modalExpiresAt, setModalExpiresAt] = useState<number | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const isModalLeader = useRef(false)
  const lastActivityCheckRef = useRef(Date.now())
  const isTabVisible = useRef(!document.hidden)

  const isAuthenticated = useAppSelector((state) =>
    isCookieAuthenticationEnabled
      ? state.auth.status === 'authenticated'
      : !!state.auth.accessToken,
  )

  const logoutMutation = useLogout()
  const { mutateAsync: refreshToken } = useTokenRefresh()

  const broadcastRef = useRef<((event: any) => void) | null>(null)

  const handleLogout = useCallback(() => {
    setShowModal(false)

    localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.LAST_ACTIVITY)
    localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.MODAL_OPENED_AT)
    localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.MODAL_EXPIRES_AT)
    localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.LEADER_TAB)
    localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.TOKEN_ISSUED_AT)
    localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.TOKEN_EXPIRES_AT)
    localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.REFRESH_LOCK)

    if (broadcastRef.current) {
      broadcastRef.current({ type: 'LOGOUT' })
    }

    logoutMutation.mutate()
  }, [logoutMutation])

  const handleTimeoutLogout = useCallback(() => {
    localStorage.setItem('session-timeout-logout', String(Date.now()))

    handleLogout()
  }, [handleLogout])

  useEffect(() => {
    const timeoutLogoutTimestamp = localStorage.getItem(
      'session-timeout-logout',
    )

    if (timeoutLogoutTimestamp) {
      const timestamp = Number(timeoutLogoutTimestamp)
      const age = Date.now() - timestamp

      if (age < 10000) {
        notifications.show({
          id: 'session-timeout',
          title: 'Session Expired',
          message:
            'You have been logged out due to inactivity. Please log in again to continue.',
          color: 'orange',
          autoClose: false,
          withCloseButton: true,
        })

        setTimeout(() => {
          localStorage.removeItem('session-timeout-logout')
        }, 3000)
      } else {
        localStorage.removeItem('session-timeout-logout')
      }
    }
  }, [])

  const handleModalClose = useCallback(() => {
    setShowModal(false)
    setModalExpiresAt(null)
    localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.MODAL_OPENED_AT)
    localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.MODAL_EXPIRES_AT)
    localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.LEADER_TAB)
    isModalLeader.current = false
  }, [])

  const hideModalLocally = useCallback(() => {
    setShowModal(false)
    setModalExpiresAt(null)
    isModalLeader.current = false
  }, [])

  const idleTimerRef = useRef<{ reset: () => void; activate: () => void }>({
    reset: () => {},
    activate: () => {},
  })

  const { broadcast, updateActivity } = useTabSync({
    onResetTimer: () => {
      idleTimerRef.current.reset()
      if (showModal) {
        handleModalClose()
      }
    },
    onLogout: () => {
      setShowModal(false)
    },
    onHideModal: () => {
      hideModalLocally()
    },
  })

  broadcastRef.current = broadcast

  const onIdle = useCallback(() => {
    if (!isAuthenticated) return

    const currentLeader = localStorage.getItem(
      SESSION_CONFIG.STORAGE_KEYS.LEADER_TAB,
    )

    if (currentLeader && currentLeader !== TAB_ID && !isTabVisible.current) {
      isModalLeader.current = false
      return
    }

    const shouldTakeLeadership = !currentLeader || isTabVisible.current

    if (shouldTakeLeadership) {
      localStorage.setItem(SESSION_CONFIG.STORAGE_KEYS.LEADER_TAB, TAB_ID)
    }

    isModalLeader.current = true

    const expiresAt = Date.now() + SESSION_CONFIG.MODAL_AUTO_LOGOUT

    setShowModal(true)
    setModalExpiresAt(expiresAt)
    setCountdown(60)

    localStorage.setItem(
      SESSION_CONFIG.STORAGE_KEYS.MODAL_OPENED_AT,
      String(Date.now()),
    )
    localStorage.setItem(
      SESSION_CONFIG.STORAGE_KEYS.MODAL_EXPIRES_AT,
      String(expiresAt),
    )

    broadcast({ type: 'SHOW_MODAL' })
  }, [isAuthenticated, broadcast])

  const onAction = useCallback(() => {
    if (!isAuthenticated) return

    updateActivity()
  }, [isAuthenticated, updateActivity])

  const { reset, activate } = useIdleTimer({
    onIdle,
    onAction,
    timeout: SESSION_CONFIG.INACTIVITY_TIMEOUT,
    throttle: 500,
    eventsThrottle: 200,
    startOnMount: isAuthenticated,
    startManually: !isAuthenticated,
    crossTab: false,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
    ],
  })

  idleTimerRef.current = { reset, activate }

  useEffect(() => {
    if (isAuthenticated) {
      const existingActivity = localStorage.getItem(
        SESSION_CONFIG.STORAGE_KEYS.LAST_ACTIVITY,
      )

      if (!existingActivity) {
        const now = Date.now()
        localStorage.setItem(
          SESSION_CONFIG.STORAGE_KEYS.LAST_ACTIVITY,
          String(now),
        )
      }
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) return

    const doProactiveRefresh = async () => {
      const tokenIssuedAt = Number(
        localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.TOKEN_ISSUED_AT) || 0,
      )
      const tokenAge = Date.now() - tokenIssuedAt

      if (tokenAge < SESSION_CONFIG.MIN_TOKEN_AGE_FOR_REFRESH) {
        return
      }

      const lockAcquired = acquireRefreshLock()
      if (!lockAcquired) {
        return
      }

      try {
        setIsRefreshing(true)

        const tokens = await refreshToken()

        if (isTokenAuthResponse(tokens) && tokens.refresh_token) {
          broadcast({
            type: 'TOKEN_REFRESH',
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
          })
        } else {
          broadcast({ type: 'TOKEN_REFRESH' })
        }

        const expiresAt = Date.now() + SESSION_CONFIG.ACCESS_TOKEN_LIFETIME
        localStorage.setItem(
          SESSION_CONFIG.STORAGE_KEYS.TOKEN_EXPIRES_AT,
          String(expiresAt),
        )
      } catch (error: any) {
        console.error(
          `%c[SessionTimeout] ❌ ERROR EN REFRESH`,
          'background: #ef4444; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
          '\n  ⚠️  Error:',
          error,
        )
      } finally {
        releaseRefreshLock()
        setIsRefreshing(false)
      }
    }

    let intervalId: NodeJS.Timeout | null = null

    const initialDelay = setTimeout(() => {
      doProactiveRefresh()

      intervalId = setInterval(
        doProactiveRefresh,
        SESSION_CONFIG.AUTO_REFRESH_INTERVAL,
      )
    }, SESSION_CONFIG.INITIAL_REFRESH_DELAY)

    return () => {
      clearTimeout(initialDelay)
      if (intervalId) clearInterval(intervalId)
    }
  }, [isAuthenticated, refreshToken, broadcast])

  useEffect(() => {
    if (!isAuthenticated) return

    const checkForSleep = () => {
      const now = Date.now()
      const elapsed = now - lastActivityCheckRef.current

      if (elapsed > SESSION_CONFIG.SLEEP_DETECTION_THRESHOLD) {
        const lastActivity = Number(
          localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.LAST_ACTIVITY) ||
            now,
        )
        const totalInactivity = now - lastActivity

        if (totalInactivity >= SESSION_CONFIG.ACCESS_TOKEN_LIFETIME) {
          handleTimeoutLogout()
          return
        }
      }

      lastActivityCheckRef.current = now
    }

    const handleVisibilityChange = () => {
      isTabVisible.current = !document.hidden

      if (!document.hidden) {
        checkForSleep()

        const modalExpiresAt = localStorage.getItem(
          SESSION_CONFIG.STORAGE_KEYS.MODAL_EXPIRES_AT,
        )
        const currentLeader = localStorage.getItem(
          SESSION_CONFIG.STORAGE_KEYS.LEADER_TAB,
        )

        if (modalExpiresAt && currentLeader && currentLeader !== TAB_ID) {
          localStorage.setItem(SESSION_CONFIG.STORAGE_KEYS.LEADER_TAB, TAB_ID)
          isModalLeader.current = true

          const expiresAt = Number(modalExpiresAt)
          setModalExpiresAt(expiresAt)
          setShowModal(true)

          const remaining = expiresAt - Date.now()
          const seconds = Math.ceil(remaining / 1000)
          setCountdown(Math.max(0, seconds))

          broadcast({ type: 'HIDE_MODAL' })
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', checkForSleep)

    const interval = setInterval(checkForSleep, 30000)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', checkForSleep)
      clearInterval(interval)
    }
  }, [isAuthenticated, refreshToken, handleTimeoutLogout])

  useEffect(() => {
    if (!modalExpiresAt || !showModal) return

    const interval = setInterval(() => {
      const remaining = Math.max(0, modalExpiresAt - Date.now())
      const seconds = Math.ceil(remaining / 1000)

      setCountdown(seconds)

      if (remaining <= 0) {
        handleTimeoutLogout()
        clearInterval(interval)
      }
    }, 100)

    return () => clearInterval(interval)
  }, [modalExpiresAt, showModal, handleTimeoutLogout])

  const handleContinue = async () => {
    setIsRefreshing(true)

    try {
      const tokens = await refreshToken()

      updateActivity()

      if (isTokenAuthResponse(tokens) && tokens.refresh_token) {
        broadcast({
          type: 'TOKEN_REFRESH',
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
        })
      } else {
        broadcast({ type: 'TOKEN_REFRESH' })
      }

      handleModalClose()

      reset()

      broadcast({ type: 'RESET_TIMER' })
    } catch (error: any) {
      if (error.response?.status === 401) {
        handleLogout()
      }
    } finally {
      setIsRefreshing(false)
    }
  }

  return (
    <>
      {children}

      <AppModal
        opened={showModal && isModalLeader.current}
        onClose={() => {}}
        withCloseButton={false}
        size="md"
        overlayProps={{ opacity: 0.7, blur: 3 }}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Stack align="center" gap="xl" p="lg">
          <Flex
            align="center"
            justify="center"
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: 'var(--mantine-color-blue-6)',
            }}
          >
            <Icons.Clock width={35} height={35} style={{ color: 'white' }} />
          </Flex>

          <Stack align="center" gap="xs">
            <Text size="xl" fw={700} ta="center">
              You're about to be signed out!
            </Text>

            <Text size="md" ta="center">
              You've been inactive for a while. You will be logged out in{' '}
              <Text span c="var(--mantine-color-red-6)" fw={700}>
                {countdown}
              </Text>{' '}
              seconds.
            </Text>
          </Stack>

          <Flex justify="center" gap="md" w="100%">
            <Button
              variant="outline"
              color="gray"
              onClick={handleLogout}
              size="lg"
              fullWidth
              disabled={isRefreshing}
            >
              Log Out
            </Button>

            <Button
              variant="filled"
              onClick={handleContinue}
              size="lg"
              fullWidth
              loading={isRefreshing}
            >
              Stay Logged In
            </Button>
          </Flex>
        </Stack>
      </AppModal>
    </>
  )
}

const acquireRefreshLock = (): boolean => {
  const lock = localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.REFRESH_LOCK)

  if (lock) {
    const lockTime = Number(lock)
    const isExpired =
      Date.now() - lockTime > SESSION_CONFIG.REFRESH_LOCK_TIMEOUT

    if (!isExpired) {
      return false
    }
  }

  localStorage.setItem(
    SESSION_CONFIG.STORAGE_KEYS.REFRESH_LOCK,
    String(Date.now()),
  )
  return true
}

const releaseRefreshLock = () => {
  localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.REFRESH_LOCK)
}
